import { useMemo, useSyncExternalStore } from 'react';

/**
 * 비로그인 참가자가 이번 탭에서 받은/쓴 젤리. 서버에 계정이 없어 세션이 대신 든다.
 *
 * ## `playerHashId` 경계 규칙
 *
 * `playerHashId` 는 Core(Script) 가 준 게스트 신원이다. 여기서 하는 유일한 일은
 * **"같은 브라우저에서 게스트가 바뀌었나"** 판정이며, 그 목적에는 정당한 값이다 —
 * 게스트는 `localStorage['@p@@ze__guest_session_id']` 의 UUID 로 `GUEST_<uuid>` 를 받고,
 * Core 가 그 값을 재사용하므로 새로고침·재접속·탭 이동에도 유지된다.
 * **비교를 없애지 말 것**: 없애면 새 게스트가 이전 게스트의 잔액과 구매를 물려받는다.
 *
 * 다만 이 식별자가 퀴즈 앱 전체로 퍼지지는 않게 한다. 받는 곳은 `PlayIframe` 의
 * postMessage 핸들러 하나뿐이고, 거기가 Core 경계다.
 *
 * - 새 값을 받는 API: `resetGuestPlayJellyBalance` / `addGuestPlayJellyBalance` 뿐
 * - 그 외(`spend`, `addOwnedShape`, 훅)는 **인자로 받지 않는다.** 스토어가 현재 세션을
 *   이미 알고 있어서, 호출부가 hash 를 다시 넘기면 Core 식별자만 퍼진다
 * - 소비처(`AvatarPartSelector` 등)에 `playerHashId` 를 다시 들이지 말 것
 */
export type GuestPlayJellyBalance = {
  playerHashId: string;
  balance: number;
};

const GUEST_PLAY_JELLY_BALANCE_KEY = 'zep.quiz.guestPlayJellyBalance';
const GUEST_PLAY_JELLY_OWNED_SHAPES_KEY = 'zep.quiz.guestPlayJellyOwnedShapes';
const GUEST_PLAY_JELLY_BALANCE_EVENT = 'zep:guest-play-jelly-balance';

/**
 * 마지막으로 읽은 원문과 그때 만든 객체.
 *
 * ⚠️ `useSyncExternalStore` 의 `getSnapshot` 은 **같은 상태면 같은 참조**를 돌려줘야
 * 한다. React 는 렌더마다 이 함수를 부르고 `Object.is` 로 직전 값과 비교하는데, 매번
 * 새 객체를 만들면 영원히 "바뀌었다"가 되어 무한 렌더로 터진다.
 *
 * 저장된 값이 없을 때는 `null` 이라 안전하지만, 게스트는 입장 시
 * `resetGuestPlayJellyBalance` 로 `{balance: 0}` 이 즉시 쓰인다 — 그래서 이 캐시가
 * 없으면 **비로그인만** 아바타 꾸미기를 열자마자 튕겼다.
 */
let cachedRaw: string | null = null;
let cachedValue: GuestPlayJellyBalance | null = null;
let cachedOwnedRaw: string | null = null;
let cachedOwnedValue: { playerHashId: string; shapeIds: number[] } | null =
  null;

const parseGuestPlayJellyBalance = (
  raw: string,
): GuestPlayJellyBalance | null => {
  try {
    const parsed = JSON.parse(raw) as Partial<GuestPlayJellyBalance>;
    if (
      typeof parsed.playerHashId !== 'string' ||
      !parsed.playerHashId ||
      typeof parsed.balance !== 'number' ||
      !Number.isFinite(parsed.balance) ||
      parsed.balance < 0
    ) {
      return null;
    }

    return { playerHashId: parsed.playerHashId, balance: parsed.balance };
  } catch {
    return null;
  }
};

const readGuestPlayJellyBalance = (): GuestPlayJellyBalance | null => {
  if (typeof window === 'undefined') return null;

  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(GUEST_PLAY_JELLY_BALANCE_KEY);
  } catch {
    return null;
  }

  if (!raw) {
    cachedRaw = null;
    cachedValue = null;
    return null;
  }

  // 원문이 그대로면 직전 객체를 그대로 돌려준다(참조 동일성 유지).
  if (raw === cachedRaw) return cachedValue;

  cachedRaw = raw;
  cachedValue = parseGuestPlayJellyBalance(raw);
  return cachedValue;
};

const writeGuestPlayJellyBalance = (value: GuestPlayJellyBalance | null) => {
  if (typeof window === 'undefined') return;

  try {
    if (value) {
      window.sessionStorage.setItem(
        GUEST_PLAY_JELLY_BALANCE_KEY,
        JSON.stringify(value),
      );
    } else {
      window.sessionStorage.removeItem(GUEST_PLAY_JELLY_BALANCE_KEY);
    }
    window.dispatchEvent(new Event(GUEST_PLAY_JELLY_BALANCE_EVENT));
  } catch {
    // sessionStorage is best-effort. The next guest grant can still render its toast.
  }
};

const readGuestPlayJellyOwnedShapes = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(
      GUEST_PLAY_JELLY_OWNED_SHAPES_KEY,
    );
    if (!raw) {
      cachedOwnedRaw = null;
      cachedOwnedValue = null;
      return null;
    }
    if (raw === cachedOwnedRaw) return cachedOwnedValue;
    const parsed = JSON.parse(raw) as {
      playerHashId?: unknown;
      shapeIds?: unknown;
    };
    cachedOwnedRaw = raw;
    cachedOwnedValue =
      typeof parsed.playerHashId === 'string' &&
      Array.isArray(parsed.shapeIds) &&
      parsed.shapeIds.every(shapeId => Number.isInteger(shapeId) && shapeId > 0)
        ? { playerHashId: parsed.playerHashId, shapeIds: parsed.shapeIds }
        : null;
    return cachedOwnedValue;
  } catch {
    return null;
  }
};

const writeGuestPlayJellyOwnedShapes = (
  value: { playerHashId: string; shapeIds: number[] } | null,
) => {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.sessionStorage.setItem(
        GUEST_PLAY_JELLY_OWNED_SHAPES_KEY,
        JSON.stringify(value),
      );
    } else {
      window.sessionStorage.removeItem(GUEST_PLAY_JELLY_OWNED_SHAPES_KEY);
    }
    window.dispatchEvent(new Event(GUEST_PLAY_JELLY_BALANCE_EVENT));
  } catch {
    // sessionStorage is best-effort.
  }
};

export const resetGuestPlayJellyBalance = (playerHashId: string) => {
  const current = readGuestPlayJellyBalance();
  if (current?.playerHashId === playerHashId) return;
  writeGuestPlayJellyOwnedShapes(null);
  writeGuestPlayJellyBalance({ playerHashId, balance: 0 });
};

export const addGuestPlayJellyBalance = (
  playerHashId: string,
  amount: number,
) => {
  if (!Number.isFinite(amount) || amount <= 0) return;

  const current = readGuestPlayJellyBalance();
  const balance =
    (current?.playerHashId === playerHashId ? current.balance : 0) + amount;
  writeGuestPlayJellyBalance({ playerHashId, balance });
  // 적립 후 잔액을 돌려준다. 호출부(수신 계측)가 같은 계산을 다시 하면 저장 규칙이
  // 바뀔 때 조용히 갈라진다.
  return balance;
};

/** 현재 게스트 세션에서 차감한다. 세션이 없으면(로그인 뒤 등) 아무것도 하지 않는다. */
export const spendGuestPlayJellyBalance = (amount: number) => {
  if (!Number.isFinite(amount) || amount <= 0) return;

  const current = readGuestPlayJellyBalance();
  if (!current) return;
  writeGuestPlayJellyBalance({
    playerHashId: current.playerHashId,
    balance: Math.max(0, current.balance - amount),
  });
};

export const clearGuestPlayJellyBalance = () => {
  writeGuestPlayJellyOwnedShapes(null);
  writeGuestPlayJellyBalance(null);
};

/**
 * claim 뒤에는 잔액만 지운다.
 *
 * 남은 owned shape snapshot 은 이 시점부터 아무 효력이 없다 —
 * `useGuestPlayJellyOwnedShapeIds` 가 로그인 상태에서 꺼지기 때문이다. 다음 게스트 세션이
 * 시작될 때 `resetGuestPlayJellyBalance` 가 함께 지운다. 굳이 여기서 지우지 않는 것은
 * claim 직후 아직 게스트 UI 가 떠 있을 수 있어 표시가 튀는 것을 피하기 위해서다.
 */
export const clearGuestPlayJellyBalanceOnly = () => {
  writeGuestPlayJellyBalance(null);
};

/**
 * 승격 요청에 실을 게스트 구매 shapeId. 훅이 아니라 claim effect 에서 부르므로 함수로 둔다.
 *
 * 서버 `shapeId` 는 문자열이고 이 snapshot 은 카탈로그의 숫자 id 를 들고 있어 변환해서 넘긴다.
 */
export const readGuestPlayJellyOwnedShapeIdsForClaim = (): string[] => {
  const owned = readGuestPlayJellyOwnedShapes();
  if (!owned) return [];
  return owned.shapeIds.map(String);
};

/**
 * 현재 게스트 세션의 구매 목록에 추가한다.
 *
 * 세션 marker 는 잔액 snapshot 에서 가져온다 — 둘은 같은 세션에 속해야 하고,
 * `resetGuestPlayJellyBalance` 가 세션 교체 시 둘을 함께 갈아치운다.
 * 잔액 snapshot 이 없으면(로그인 뒤 등) 게스트 구매가 성립하지 않으므로 무시한다.
 */
export const addGuestPlayJellyOwnedShape = (shapeId: number) => {
  if (!Number.isInteger(shapeId) || shapeId <= 0) return;
  const session = readGuestPlayJellyBalance();
  if (!session) return;
  const current = readGuestPlayJellyOwnedShapes();
  const shapeIds =
    current?.playerHashId === session.playerHashId
      ? [...new Set([...current.shapeIds, shapeId])]
      : [shapeId];
  writeGuestPlayJellyOwnedShapes({
    playerHashId: session.playerHashId,
    shapeIds,
  });
};

const subscribe = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') return () => undefined;

  window.addEventListener(GUEST_PLAY_JELLY_BALANCE_EVENT, onStoreChange);
  window.addEventListener('storage', onStoreChange);
  return () => {
    window.removeEventListener(GUEST_PLAY_JELLY_BALANCE_EVENT, onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
};

/**
 * 테스트 전용. 훅이 `getSnapshot` 으로 넘기는 것과 **같은 함수**를 노출한다 —
 * 참조 동일성이 깨지는 지점이 여기라, 훅을 렌더하지 않고 계약만 확인한다.
 */
export const readGuestPlayJellyBalanceForTest = readGuestPlayJellyBalance;
export const readGuestPlayJellyOwnedShapesForTest =
  readGuestPlayJellyOwnedShapes;

/** 비로그인 참가자의 현재 플레이 세션 미수령 젤리 합계. */
export const useGuestPlayJellyBalance = () =>
  useSyncExternalStore(subscribe, readGuestPlayJellyBalance, () => null);

/**
 * 이번 탭의 게스트가 산 파츠를 반환한다.
 *
 * ⚠️ `enabled` 는 **비로그인일 때만 true** 여야 한다. 게스트 구매는 서버를 거치지
 * 않고(`AvatarPartSelector` 는 `!isGuest` 일 때만 purchase mutation 을 부른다) 이
 * snapshot 에만 남는다. 로그인 뒤에도 카탈로그에 덮으면 **학생이 사지도 않은 파츠를
 * 보유한 것처럼 선택·저장**하게 된다 — 서버 소유권과 어긋난 아바타가 저장되는
 * 제보의 원인이었다. 로그인 뒤에는 실제 계정으로 다시 구매하는 것이 설계다.
 *
 * 게스트가 바뀌는 경우는 여기서 보지 않는다. `resetGuestPlayJellyBalance` 가 write
 * 시점에 이전 게스트의 snapshot 을 지우므로, read 에서 소유자를 다시 대조할 필요가
 * 없다. (Core 의 `playerHashId` 를 퀴즈 앱 판정에 끌어들이지 않기 위한 선택이다.)
 */
export const useGuestPlayJellyOwnedShapeIds = (enabled: boolean) => {
  const owned = useSyncExternalStore(
    subscribe,
    readGuestPlayJellyOwnedShapes,
    () => null,
  );
  // useMemo 필수: 매 렌더 새 Set 을 만들면 이 값을 의존성으로 갖는 `shapes` useMemo 가
  // 항상 재계산되고, 선택 동기화 effect 가 다시 렌더를 유발해 무한 렌더로 터진다.
  return useMemo(
    () => (enabled && owned ? new Set(owned.shapeIds) : new Set<number>()),
    [owned, enabled],
  );
};
